import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { firebaseService } from '../../services/firebaseService';
import { Search, Filter, MoreHorizontal, Plus, Download, Printer, Edit, Trash2, Eye, UserX } from 'lucide-react';
import AssinaturaModal from '../../components/cobrancas/AssinaturaModal';
import { useNotification } from '../../hooks/useNotification';

interface AssinaturaCobranca {
  id: string;
  alunoId: string;
  alunoNome: string;
  valor: number;
  descricao: string;
  formaPagamento: string;
  proximoVencimento: string;
  status: 'ACTIVE' | 'INACTIVE' | 'CANCELLED';
  ciclo: string;
  dataInicio: string;
  dataFim?: string;
  alunoInativo?: boolean;
}

const AssinaturasCobrancas: React.FC = () => {
  const navigate = useNavigate();
  const { userProfile } = useAuth();
  const { showSuccess, showError, showConfirm, showInfo, NotificationComponent } = useNotification();
  const [assinaturas, setAssinaturas] = useState<AssinaturaCobranca[]>([]);
  const [todosAlunos, setTodosAlunos] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [showBatchActions, setShowBatchActions] = useState(false);
  const [showAssinaturaModal, setShowAssinaturaModal] = useState(false);
  const [selectedAssinaturaId, setSelectedAssinaturaId] = useState<string | undefined>();
  const [preloadedData, setPreloadedData] = useState<{alunoId: string, valor: number, diaVencimento: number} | undefined>();
  const [filters, setFilters] = useState({
    ciclo: '',
    status: '',
    dataInicio: '',
    dataFim: '',
    semAssinaturas: false,
    apenasInativos: false
  });
  const [selectedAssinaturas, setSelectedAssinaturas] = useState<string[]>([]);

  const loadAssinaturas = React.useCallback(async () => {
    if (!userProfile?.empresaCnpj) return;
    
    try {
      setLoading(true);
      const [assinaturasData, alunos] = await Promise.all([
        firebaseService.getAssinaturas(userProfile.empresaCnpj),
        firebaseService.getAllAlunos(userProfile.empresaCnpj)
      ]);

      const assinaturasWithAlunos = assinaturasData.map(assinatura => {
        const aluno = alunos.find(a => a.id === assinatura.alunoId);
        return {
          id: assinatura.id,
          alunoId: assinatura.alunoId,
          alunoNome: aluno?.nome || 'Cliente não encontrado',
          valor: typeof assinatura.value === 'string' ? parseFloat(assinatura.value) : (assinatura.value || 0),
          descricao: assinatura.description,
          formaPagamento: assinatura.billingType,
          proximoVencimento: assinatura.nextDueDate,
          status: assinatura.status as 'ACTIVE' | 'INACTIVE' | 'CANCELLED',
          ciclo: assinatura.cycle,
          dataInicio: assinatura.nextDueDate || assinatura.createDate,
          dataFim: assinatura.deletedDate,
          alunoInativo: aluno ? !aluno.bAtivo : false
        };
      });

      setAssinaturas(assinaturasWithAlunos);
      setTodosAlunos(alunos);
    } catch (error) {
      console.error('Erro ao carregar assinaturas:', error);
    } finally {
      setLoading(false);
    }
  }, [userProfile?.empresaCnpj]);

  useEffect(() => {
    loadAssinaturas();
  }, [loadAssinaturas]);

  // Lógica para mostrar dados baseado no filtro "Sem assinaturas"
  const getDisplayData = (): AssinaturaCobranca[] => {
    if (filters.semAssinaturas) {
      // Se filtro "Sem assinaturas" estiver ativo, mostrar alunos sem assinaturas
      const alunosComAssinaturas = assinaturas.map(a => a.alunoId);
      const alunosSemAssinaturas = todosAlunos.filter(aluno => 
        !alunosComAssinaturas.includes(aluno.id) &&
        aluno.nome && aluno.nome.toLowerCase().includes(searchTerm.toLowerCase())
      );
      
      // Converter alunos sem assinaturas para o formato esperado
      return alunosSemAssinaturas
        .filter(aluno => {
          // Aplicar filtro de alunos inativos se ativo
          if (filters.apenasInativos) {
            return !aluno.bAtivo;
          }
          return true;
        })
        .map(aluno => ({
          id: `sem-assinatura-${aluno.id}`,
          alunoId: aluno.id,
          alunoNome: aluno.nome || 'Nome não disponível',
          valor: Number(aluno.valorMensalidade) || 0,
          descricao: 'Sem assinatura ativa',
          formaPagamento: 'N/A',
          proximoVencimento: 'N/A',
          status: 'INACTIVE' as 'ACTIVE' | 'INACTIVE' | 'CANCELLED',
          ciclo: 'N/A',
          dataInicio: aluno.createDate || new Date().toISOString(),
          dataFim: undefined,
          alunoInativo: !aluno.bAtivo
        }));
    } else {
      // Lógica normal de filtro de assinaturas
      return assinaturas.filter(assinatura => {
        const matchesSearch = assinatura.alunoNome.toLowerCase().includes(searchTerm.toLowerCase()) ||
                             assinatura.descricao.toLowerCase().includes(searchTerm.toLowerCase());
        
        const matchesCiclo = !filters.ciclo || assinatura.ciclo === filters.ciclo;
        const matchesStatus = !filters.status || assinatura.status === filters.status;
        
        const matchesDataInicio = !filters.dataInicio || new Date(assinatura.dataInicio) >= new Date(filters.dataInicio);
        const matchesDataFim = !filters.dataFim || new Date(assinatura.dataInicio) <= new Date(filters.dataFim);
        
        // Filtro para mostrar apenas alunos inativos
        const matchesInativos = !filters.apenasInativos || assinatura.alunoInativo === true;
        
        return matchesSearch && matchesCiclo && matchesStatus && matchesDataInicio && matchesDataFim && matchesInativos;
      });
    }
  };

  const filteredAssinaturas: AssinaturaCobranca[] = getDisplayData();


  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value);
  };


  const totalValue = filteredAssinaturas.reduce((sum, assinatura) => sum + assinatura.valor, 0);
  const activeSubscriptions = filteredAssinaturas.filter(a => a.status === 'ACTIVE').length;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <nav className="flex" aria-label="Breadcrumb">
            <ol className="flex items-center space-x-2">
              <li>
                <span className="text-gray-500">Cobranças</span>
              </li>
              <li>
                <span className="text-gray-400">/</span>
              </li>
              <li>
                <span className="text-gray-900 font-medium">Assinaturas</span>
              </li>
            </ol>
          </nav>
          <h1 className="text-2xl font-bold text-gray-900 mt-2">Assinaturas</h1>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4">
          <div className="text-sm font-medium text-gray-500">Total de Assinaturas</div>
          <div className="text-2xl font-bold text-gray-900">{filteredAssinaturas.length}</div>
        </div>
        <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4">
          <div className="text-sm font-medium text-gray-500">Assinaturas Ativas</div>
          <div className="text-2xl font-bold text-green-600">{activeSubscriptions}</div>
        </div>
        <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4">
          <div className="text-sm font-medium text-gray-500">Valor Total Mensal</div>
          <div className="text-2xl font-bold text-gray-900">{formatCurrency(totalValue)}</div>
        </div>
      </div>

      {/* Search and Actions */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
            <input
              type="text"
              placeholder="Procurar por nome ou descrição"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-primary-500 w-80"
            />
          </div>
          
          <button
            onClick={() => setShowFilters(!showFilters)}
            className="flex items-center space-x-2 px-4 py-2 border border-primary-300 text-primary-600 rounded-md hover:bg-primary-50"
          >
            <Filter className="h-4 w-4" />
            <span>Filtros</span>
          </button>
          
          <label className="flex items-center space-x-2 px-3 py-2 border border-gray-300 rounded-md hover:bg-gray-50 cursor-pointer">
            <input 
              type="checkbox" 
              checked={filters.semAssinaturas}
              onChange={(e) => setFilters(prev => ({ ...prev, semAssinaturas: e.target.checked }))}
              className="rounded border-gray-300 text-primary-600 focus:ring-primary-500" 
            />
            <span className="text-sm text-gray-700">Sem assinaturas</span>
          </label>
          
          <label className="flex items-center space-x-2 px-3 py-2 border border-gray-300 rounded-md hover:bg-gray-50 cursor-pointer">
            <input 
              type="checkbox" 
              checked={filters.apenasInativos}
              onChange={(e) => setFilters(prev => ({ ...prev, apenasInativos: e.target.checked }))}
              className="rounded border-gray-300 text-primary-600 focus:ring-primary-500" 
            />
            <span className="text-sm text-gray-700">Apenas alunos inativos</span>
          </label>
        </div>

        <div className="flex items-center space-x-3">
          <div className="relative">
            <button
              onClick={() => setShowBatchActions(!showBatchActions)}
              className="flex items-center space-x-2 px-4 py-2 border border-primary-300 text-primary-600 rounded-md hover:bg-primary-50"
            >
              <MoreHorizontal className="h-4 w-4" />
              <span>Ações em lote</span>
            </button>
            
            {showBatchActions && (
              <div className="absolute right-0 mt-2 w-48 bg-white dark:bg-gray-800 rounded-md shadow-lg border border-gray-200 dark:border-gray-700 z-10">
                <div className="py-1">
                  <button 
                    onClick={() => {
                      if (selectedAssinaturas.length === 0) {
                        showError('Atenção', 'Selecione pelo menos uma assinatura');
                        return;
                      }
                      showInfo('Informação', `Modificando ${selectedAssinaturas.length} assinaturas`);
                    }}
                    className="flex items-center space-x-2 w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                  >
                    <Edit className="h-4 w-4" />
                    <span>Modificar assinaturas</span>
                  </button>
                  <button 
                    onClick={() => {
                      if (selectedAssinaturas.length === 0) {
                        showError('Atenção', 'Selecione pelo menos uma assinatura');
                        return;
                      }
                      showInfo('Informação', `Exportando ${selectedAssinaturas.length} assinaturas`);
                    }}
                    className="flex items-center space-x-2 w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                  >
                    <Download className="h-4 w-4" />
                    <span>Exportar dados</span>
                  </button>
                </div>
              </div>
            )}
          </div>

          <button 
            onClick={() => {
              setSelectedAssinaturaId(undefined);
              setShowAssinaturaModal(true);
            }}
            className="flex items-center space-x-2 px-4 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700"
          >
            <Plus className="h-4 w-4" />
            <span>Adicionar assinatura</span>
          </button>
        </div>
      </div>

      {/* Filters */}
      {showFilters && (
        <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Intervalos
              </label>
              <select 
                value={filters.ciclo}
                onChange={(e) => setFilters(prev => ({ ...prev, ciclo: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              >
                <option value="">Todas os intervalos</option>
                <option value="MONTHLY">Mensal</option>
                <option value="QUARTERLY">Trimestral</option>
                <option value="SEMIANNUAL">Semestral</option>
                <option value="ANNUAL">Anual</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Situação de cobranças
              </label>
              <select 
                value={filters.status}
                onChange={(e) => setFilters(prev => ({ ...prev, status: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              >
                <option value="">Todas situações de cobrança</option>
                <option value="ACTIVE">Ativa</option>
                <option value="INACTIVE">Inativa</option>
                <option value="CANCELLED">Cancelada</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Data de criação
              </label>
              <div className="flex items-center space-x-2">
                <input
                  type="date"
                  value={filters.dataInicio}
                  onChange={(e) => setFilters(prev => ({ ...prev, dataInicio: e.target.value }))}
                  className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                />
                <span className="text-gray-500">até</span>
                <input
                  type="date"
                  value={filters.dataFim}
                  onChange={(e) => setFilters(prev => ({ ...prev, dataFim: e.target.value }))}
                  className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                />
              </div>
            </div>

          </div>

          <div className="flex justify-end space-x-3 mt-6">
            <button 
              type="button"
              onClick={() => setFilters({ ciclo: '', status: '', dataInicio: '', dataFim: '', semAssinaturas: false, apenasInativos: false })}
              className="px-4 py-2 text-gray-600 border border-gray-300 rounded-md hover:bg-gray-50"
            >
              Limpar
            </button>
            <button 
              type="button"
              onClick={() => setShowFilters(false)}
              className="px-4 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700"
            >
              Aplicar
            </button>
          </div>
        </div>
      )}

      {/* Table */}
      <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  <input
                    type="checkbox"
                    checked={selectedAssinaturas.length === filteredAssinaturas.length && filteredAssinaturas.length > 0}
                    onChange={(e) => {
                      if (e.target.checked) {
                        setSelectedAssinaturas(filteredAssinaturas.map(a => a.id));
                      } else {
                        setSelectedAssinaturas([]);
                      }
                    }}
                    className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                  />
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Cliente
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Valor
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Descrição
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Forma de pagamento
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Dia Vencimento
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Ações
                </th>
              </tr>
            </thead>
            <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
              {filteredAssinaturas.map((assinatura) => (
                <tr key={assinatura.id} className="hover:bg-gray-50">
                  <td className="px-4 py-4 whitespace-nowrap">
                    <input
                      type="checkbox"
                      checked={selectedAssinaturas.includes(assinatura.id)}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setSelectedAssinaturas(prev => [...prev, assinatura.id]);
                        } else {
                          setSelectedAssinaturas(prev => prev.filter(id => id !== assinatura.id));
                        }
                      }}
                      className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                    />
                  </td>
                  <td className="px-4 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <div className={`h-8 w-8 rounded-full flex items-center justify-center mr-3 ${
                        assinatura.alunoInativo ? 'bg-gray-200 dark:bg-gray-600' : 'bg-green-100'
                      }`}>
                        <span className={`text-sm font-medium ${
                          assinatura.alunoInativo ? 'text-gray-500 dark:text-gray-400' : 'text-green-600'
                        }`}>
                          {assinatura.alunoNome.charAt(0).toUpperCase()}
                        </span>
                      </div>
                      <div className="flex items-center space-x-2">
                        <div className="text-sm font-medium text-gray-900">{assinatura.alunoNome}</div>
                        {assinatura.alunoInativo && (
                          <div title="Aluno inativo">
                            <UserX 
                              className="h-4 w-4 text-gray-400 dark:text-gray-500" 
                            />
                          </div>
                        )}
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-4 whitespace-nowrap">
                    <div className="text-sm font-medium text-gray-900">
                      {formatCurrency(assinatura.valor)}
                    </div>
                  </td>
                  <td className="px-4 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900">{assinatura.descricao}</div>
                  </td>
                  <td className="px-4 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900">{assinatura.formaPagamento}</div>
                  </td>
                  <td className="px-4 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900">
                      {todosAlunos.find(aluno => aluno.id === assinatura.alunoId)?.diaVencimento || 'N/A'}
                    </div>
                  </td>
                  <td className="px-4 py-4 whitespace-nowrap text-sm font-medium">
                    <div className="flex items-center space-x-2">
                      {assinatura.id.startsWith('sem-assinatura-') ? (
                        // Botões para alunos sem assinatura
                        <button 
                          onClick={() => {
                            const aluno = todosAlunos.find(a => a.id === assinatura.alunoId);
                            if (aluno) {
                              setPreloadedData({
                                alunoId: assinatura.alunoId,
                                valor: Number(aluno.valorMensalidade) || 0,
                                diaVencimento: aluno.diaVencimento || 1
                              });
                            }
                            setSelectedAssinaturaId(undefined);
                            setShowAssinaturaModal(true);
                          }}
                          className="text-green-600 hover:text-green-900"
                          title="Criar assinatura para este aluno"
                        >
                          <Plus className="h-4 w-4" />
                        </button>
                      ) : (
                        // Botões para assinaturas normais
                        <>
                          <button 
                            onClick={() => navigate(`/cobrancas/assinaturas/${assinatura.id}`)}
                            className="text-primary-600 hover:text-primary-900"
                            title="Ver detalhes da assinatura"
                          >
                            <Eye className="h-4 w-4" />
                          </button>
                          <button 
                            onClick={() => {
                              setSelectedAssinaturaId(assinatura.id);
                              setShowAssinaturaModal(true);
                            }}
                            className="text-primary-600 hover:text-primary-900"
                            title="Editar assinatura"
                          >
                            <Edit className="h-4 w-4" />
                          </button>
                          <button 
                            onClick={() => {
                              showInfo('Informação', `Imprimindo assinatura: ${assinatura.alunoNome}`);
                            }}
                            className="text-primary-600 hover:text-primary-900"
                            title="Imprimir carnê"
                          >
                            <Printer className="h-4 w-4" />
                          </button>
                          <button 
                            onClick={() => {
                              showConfirm(
                                'Excluir Assinatura',
                                `Tem certeza que deseja excluir a assinatura de ${assinatura.alunoNome}?`,
                                async () => {
                                  try {
                                    await firebaseService.deleteAssinatura(userProfile?.empresaCnpj || '', assinatura.id);
                                    loadAssinaturas();
                                    showSuccess('Sucesso', 'Assinatura excluída com sucesso!');
                                  } catch (error) {
                                    console.error('Erro ao excluir assinatura:', error);
                                    showError('Erro', 'Erro ao excluir assinatura. Tente novamente.');
                                  }
                                },
                                {
                                  confirmText: 'Excluir',
                                  cancelText: 'Cancelar'
                                }
                              );
                            }}
                            className="text-red-600 hover:text-red-900"
                            title="Excluir assinatura"
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {filteredAssinaturas.length === 0 && (
        <div className="text-center py-12">
          <div className="text-gray-500 text-lg">Nenhuma assinatura encontrada</div>
          <div className="text-gray-400 text-sm mt-2">
            {searchTerm ? 'Tente ajustar os filtros de busca' : 'Comece criando sua primeira assinatura'}
          </div>
        </div>
      )}

      {/* Modal de Assinatura */}
      <AssinaturaModal
        isOpen={showAssinaturaModal}
        onClose={() => {
          setShowAssinaturaModal(false);
          setPreloadedData(undefined);
        }}
        assinaturaId={selectedAssinaturaId}
        preloadedData={preloadedData}
        onSuccess={() => {
          loadAssinaturas();
          setShowAssinaturaModal(false);
          setPreloadedData(undefined);
        }}
      />

      <NotificationComponent />
    </div>
  );
};

export default AssinaturasCobrancas;
